import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, LogOut, X, CheckCircle2, Circle, Trash2, Users, Folder, Inbox, FileText, Briefcase, Sparkles, Radar, Settings2, FlaskConical } from "lucide-react";
import { siteSections } from "@/data/siteMap";
import { ProposalPanel } from "@/components/portal/ProposalPanel";
import { ThemeToggle } from "@/components/ThemeContext";
import ScoutPulse from "@/components/scout/ScoutPulse";

const STATUSES = ["planning", "active", "paused", "shipped"];
const INSIGHT_CATEGORIES = siteSections.insights.items;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [applications, setApplications] = useState([]);
  const [proposalClientId, setProposalClientId] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [showNewInsight, setShowNewInsight] = useState(false);
  const [editInsight, setEditInsight] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p, s, i, a] = await Promise.all([
        api.get("/clients"),
        api.get("/projects"),
        api.get("/submissions").catch(() => ({ data: [] })),
        api.get("/admin/insights").catch(() => ({ data: [] })),
        api.get("/applications").catch(() => ({ data: [] })),
      ]);
      setClients(c.data);
      setProjects(p.data);
      setSubmissions(s.data);
      setInsights(i.data);
      setApplications(a.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const clientById = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clients]);

  return (
    <div className="bg-[#0B0C10] min-h-[80vh]" data-testid="admin-dashboard">
      <section className="border-b border-[#1F2833] py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// ADMIN.CONSOLE</div>
            <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">
              Operations <span className="text-[#FF3355]">cockpit.</span>
            </h1>
            <p className="mt-3 text-sm text-[#C5C6C7]">{user?.email} · root access</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/admin/demos"
              data-testid="admin-demos-link"
              className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#F5F5F7] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em]"
            >
              <FlaskConical size={14} /> demo factory
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#F5F5F7] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em]"
            >
              <LogOut size={14} /> sign out
            </button>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <ThemeToggle />
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="inline-flex flex-wrap border border-[#1F2833] mb-10">
            <AdminTab id="projects" tab={tab} setTab={setTab} icon={Folder} label="Projects" count={projects.length} />
            <AdminTab id="clients" tab={tab} setTab={setTab} icon={Users} label="Clients" count={clients.length} />
            <AdminTab id="proposals" tab={tab} setTab={setTab} icon={Sparkles} label="Proposals" count={null} />
            <AdminTab id="scout" tab={tab} setTab={setTab} icon={Radar} label="ScoutPulse" count={null} />
            <AdminTab id="inbox" tab={tab} setTab={setTab} icon={Inbox} label="Inbox" count={submissions.length} />
            <AdminTab id="insights" tab={tab} setTab={setTab} icon={FileText} label="Insights" count={insights.length} />
            <AdminTab id="applications" tab={tab} setTab={setTab} icon={Briefcase} label="Applications" count={applications.length} />
            <AdminTab id="toggles" tab={tab} setTab={setTab} icon={Settings2} label="Site Toggles" count={null} />
          </div>

          {loading && <div className="font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>}

          {!loading && tab === "projects" && (
            <ProjectsTab
              projects={projects}
              clients={clients}
              clientById={clientById}
              onCreate={() => setShowNewProject(true)}
              onOpen={(id) => setActiveProjectId(id)}
            />
          )}

          {!loading && tab === "clients" && (
            <ClientsTab
              clients={clients}
              onCreate={() => setShowNewClient(true)}
            />
          )}

          {!loading && tab === "proposals" && (
            <div className="space-y-6" data-testid="admin-proposals-tab">
              <div className="border border-[#1F2833] p-5">
                <div className="font-mono text-xs uppercase tracking-[0.28em] text-[#B91C2C] mb-3">// SELECT CLIENT</div>
                <select
                  value={proposalClientId}
                  onChange={(e) => setProposalClientId(e.target.value)}
                  data-testid="admin-proposal-client-select"
                  className="w-full sm:w-96 bg-slate-950 border border-[#1F2833] text-[#F5F5F7] px-3 py-2 font-mono text-sm focus:border-[#FF3355] outline-none"
                >
                  <option value="">— Choose a client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.email}</option>
                  ))}
                </select>
              </div>
              <ProposalPanel
                mode="admin"
                clientId={proposalClientId}
                clientName={(clients.find((c) => c.id === proposalClientId) || {}).name || ""}
              />
            </div>
          )}

          {!loading && tab === "inbox" && <InboxTab submissions={submissions} />}
          {!loading && tab === "scout" && <ScoutPulse />}
          {!loading && tab === "insights" && (
            <InsightsTab
              insights={insights}
              onCreate={() => setShowNewInsight(true)}
              onEdit={(i) => setEditInsight(i)}
              onDelete={async (i) => {
                if (!window.confirm(`Delete "${i.title}"?`)) return;
                try {
                  await api.delete(`/admin/insights/${i.id}`);
                  await loadAll();
                  toast.success("Insight deleted.");
                } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
              }}
            />
          )}
          {!loading && tab === "applications" && <ApplicationsTab applications={applications} />}
          {!loading && tab === "toggles" && <SiteTogglesTab />}
        </div>
      </section>

      {showNewClient && <NewClientModal onClose={() => setShowNewClient(false)} onCreated={(c) => { setClients((arr) => [c, ...arr]); setShowNewClient(false); toast.success("Client created."); }} />}
      {showNewProject && <NewProjectModal clients={clients} onClose={() => setShowNewProject(false)} onCreated={(p) => { setProjects((arr) => [p, ...arr]); setShowNewProject(false); toast.success("Project created."); }} />}
      {activeProjectId && <ProjectDrawer projectId={activeProjectId} onClose={() => setActiveProjectId(null)} onChange={loadAll} />}
      {showNewInsight && <InsightModal onClose={() => setShowNewInsight(false)} onSaved={async () => { setShowNewInsight(false); await loadAll(); toast.success("Insight published."); }} />}
      {editInsight && <InsightModal insight={editInsight} onClose={() => setEditInsight(null)} onSaved={async () => { setEditInsight(null); await loadAll(); toast.success("Insight updated."); }} />}
    </div>
  );
}

function AdminTab({ id, tab, setTab, icon: Icon, label, count }) {
  const isActive = tab === id;
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 ${
        isActive ? "bg-[#B91C2C] text-[#0B0C10]" : "text-[#C5C6C7] hover:text-[#F5F5F7]"
      }`}
    >
      <Icon size={14} /> {label} <span className="opacity-60">({count})</span>
    </button>
  );
}

/* ============ PROJECTS TAB ============ */
function ProjectsTab({ projects, clients, clientById, onCreate, onOpen }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// PROJECTS · {projects.length}</div>
        <button onClick={onCreate} data-testid="admin-new-project" disabled={clients.length === 0} className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50">
          <Plus size={14} /> new project
        </button>
      </div>
      {projects.length === 0 ? (
        <EmptyBlock title="No projects." hint={clients.length === 0 ? "Create a client first." : "Create a project to attach milestones and preview URLs."} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => onOpen(p.id)}
              data-testid={`admin-project-${p.id}`}
              className="bg-[#0B0C10] p-6 text-left te-glow-hover border border-transparent hover:border-[#B91C2C]"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] border border-[#FF3355]/50 px-2 py-0.5">{p.status}</span>
                <span className="font-mono text-[10px] text-[#C5C6C7]">{p.sprint_progress}%</span>
              </div>
              <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{p.name}</h3>
              <div className="mt-2 font-mono text-xs text-[#C5C6C7]">
                {clientById[p.client_id]?.name || "—"} · {clientById[p.client_id]?.company || "client"}
              </div>
              <div className="mt-4 h-1 bg-[#1F2833] relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#FF3355]" style={{ width: `${p.sprint_progress}%` }} />
              </div>
              <div className="mt-3 font-mono text-xs text-[#C5C6C7]">{p.milestone_completed}/{p.milestone_count} milestones</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ CLIENTS TAB ============ */
function ClientsTab({ clients, onCreate }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// CLIENTS · {clients.length}</div>
        <button onClick={onCreate} data-testid="admin-new-client" className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">
          <Plus size={14} /> new client
        </button>
      </div>
      {clients.length === 0 ? (
        <EmptyBlock title="No clients yet." hint="Create one to begin provisioning portal access." />
      ) : (
        <div className="border border-[#1F2833]">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
            <div className="col-span-4">name</div>
            <div className="col-span-4">email</div>
            <div className="col-span-3">company</div>
            <div className="col-span-1">since</div>
          </div>
          {clients.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#1F2833] last:border-b-0 font-mono text-sm" data-testid={`admin-client-${c.id}`}>
              <div className="col-span-4 text-[#F5F5F7]">{c.name}</div>
              <div className="col-span-4 text-[#C5C6C7]">{c.email}</div>
              <div className="col-span-3 text-[#C5C6C7]">{c.company || "—"}</div>
              <div className="col-span-1 text-[#C5C6C7]/60 text-xs">{new Date(c.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ INBOX TAB ============ */
function InboxTab({ submissions }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// INBOX · {submissions.length} signals</div>
      {submissions.length === 0 ? (
        <EmptyBlock title="No incoming signals yet." hint="Submissions from the public /terminal will appear here." />
      ) : (
        <div className="space-y-4">
          {submissions.map((s) => (
            <div key={s.id} className="border border-[#1F2833] p-6" data-testid={`admin-submission-${s.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-lg font-bold">{s.name}</div>
                  <div className="font-mono text-xs text-[#C5C6C7]">{s.email} {s.company ? `· ${s.company}` : ""}</div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">
                  {new Date(s.created_at).toLocaleString()}
                </div>
              </div>
              <div className="mt-3 font-mono text-xs text-[#B91C2C]">{(s.tracks || []).join(" · ") || "no tracks"} · {s.timeline || "—"}</div>
              <p className="mt-3 text-sm text-[#C5C6C7] whitespace-pre-wrap">{s.brief}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ MODALS ============ */
function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#0B0C10]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="te-terminal-window w-full max-w-2xl my-12" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355]">{title}</div>
          <button onClick={onClose} className="text-[#C5C6C7] hover:text-[#F5F5F7]" data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6 lg:p-8">{children}</div>
      </div>
    </div>
  );
}

function NewClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/clients", form);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="// NEW.CLIENT" onClose={onClose} testid="new-client-modal">
      <form onSubmit={submit} className="space-y-5">
        <Field label="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-client-name" required />
        <Field label="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="new-client-email" required />
        <Field label="password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="new-client-password" required minLength={4} />
        <Field label="company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="new-client-company" />
        <button type="submit" disabled={busy} data-testid="new-client-submit" className="w-full bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50">
          {busy ? "PROVISIONING..." : "[ CREATE CLIENT ]"}
        </button>
      </form>
    </ModalShell>
  );
}

function NewProjectModal({ clients, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", client_id: clients[0]?.id || "", status: "planning",
    description: "", sprint_progress: 0,
    preview_staging_url: "", preview_production_url: "", repository_url: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, sprint_progress: Number(form.sprint_progress) || 0 };
      ["preview_staging_url", "preview_production_url", "repository_url", "description"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      const { data } = await api.post("/projects", payload);
      onCreated({ ...data, milestone_count: 0, milestone_completed: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="// NEW.PROJECT" onClose={onClose} testid="new-project-modal">
      <form onSubmit={submit} className="space-y-5">
        <Field label="project name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-project-name" required />
        <SelectField label="client" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} testid="new-project-client" options={clients.map((c) => ({ value: c.id, label: `${c.name} · ${c.email}` }))} />
        <SelectField label="status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} testid="new-project-status" options={STATUSES.map((s) => ({ value: s, label: s }))} />
        <Field label="description (optional)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="new-project-description" textarea />
        <Field label="sprint progress (0-100)" type="number" value={form.sprint_progress} onChange={(v) => setForm({ ...form, sprint_progress: v })} testid="new-project-progress" min={0} max={100} />
        <Field label="staging url" value={form.preview_staging_url} onChange={(v) => setForm({ ...form, preview_staging_url: v })} testid="new-project-staging" />
        <Field label="production url" value={form.preview_production_url} onChange={(v) => setForm({ ...form, preview_production_url: v })} testid="new-project-production" />
        <Field label="repository url" value={form.repository_url} onChange={(v) => setForm({ ...form, repository_url: v })} testid="new-project-repo" />
        <button type="submit" disabled={busy} data-testid="new-project-submit" className="w-full bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50">
          {busy ? "CREATING..." : "[ CREATE PROJECT ]"}
        </button>
      </form>
    </ModalShell>
  );
}

function ProjectDrawer({ projectId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [milestoneTitle, setMilestoneTitle] = useState("");
  const [milestoneDue, setMilestoneDue] = useState("");
  const [activityMsg, setActivityMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [statusV, setStatusV] = useState("planning");
  const [stagingUrl, setStagingUrl] = useState("");
  const [productionUrl, setProductionUrl] = useState("");

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    setData(data);
    setProgress(data.project.sprint_progress);
    setStatusV(data.project.status);
    setStagingUrl(data.project.preview_staging_url || "");
    setProductionUrl(data.project.preview_production_url || "");
  };
  useEffect(() => { load(); }, [projectId]);

  const updateProject = async (patch) => {
    try {
      await api.patch(`/projects/${projectId}`, patch);
      await load();
      onChange?.();
      toast.success("Project updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const addMilestone = async (e) => {
    e.preventDefault();
    if (!milestoneTitle.trim()) return;
    try {
      const body = { title: milestoneTitle.trim() };
      if (milestoneDue) body.due_date = new Date(milestoneDue).toISOString();
      await api.post(`/projects/${projectId}/milestones`, body);
      setMilestoneTitle(""); setMilestoneDue("");
      await load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const toggleMilestone = async (m) => {
    try {
      await api.patch(`/milestones/${m.id}`, { completed: !m.completed });
      await load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const deleteMilestone = async (m) => {
    try {
      await api.delete(`/milestones/${m.id}`);
      await load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!activityMsg.trim()) return;
    try {
      await api.post(`/projects/${projectId}/activity`, { message: activityMsg.trim() });
      setActivityMsg("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (!data) return (
    <ModalShell title="// PROJECT.LOADING" onClose={onClose} testid="project-drawer-loading">
      <div className="font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>
    </ModalShell>
  );

  const { project, milestones, activity, client } = data;

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0C10]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="te-terminal-window w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()} data-testid="project-drawer">
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355] truncate">// {project.name}</div>
          <button onClick={onClose} className="text-[#C5C6C7] hover:text-[#F5F5F7]"><X size={18} /></button>
        </div>
        <div className="p-6 lg:p-8 space-y-8">
          <div className="font-mono text-xs text-[#C5C6C7]">
            client: <span className="text-[#F5F5F7]">{client?.name}</span> · {client?.email}
          </div>

          {/* Edit panel */}
          <div className="border border-[#1F2833] p-5 space-y-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">// EDIT</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="status" value={statusV} onChange={setStatusV} testid="drawer-status" options={STATUSES.map((s) => ({ value: s, label: s }))} />
              <Field label="sprint progress" type="number" value={progress} onChange={(v) => setProgress(v)} testid="drawer-progress" min={0} max={100} />
              <Field label="staging url" value={stagingUrl} onChange={setStagingUrl} testid="drawer-staging" />
              <Field label="production url" value={productionUrl} onChange={setProductionUrl} testid="drawer-production" />
            </div>
            <button
              onClick={() => updateProject({ status: statusV, sprint_progress: Number(progress) || 0, preview_staging_url: stagingUrl, preview_production_url: productionUrl })}
              data-testid="drawer-save"
              className="bg-[#B91C2C] text-[#0B0C10] px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]"
            >
              [ SAVE ]
            </button>
          </div>

          {/* AI Progress Update generator */}
          <AiUpdateBlock projectId={projectId} onPosted={load} />

          {/* Milestones */}
          <div className="border border-[#1F2833] p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">// MILESTONES · {milestones.length}</div>
            <ul className="divide-y divide-[#1F2833]">
              {milestones.map((m) => (
                <li key={m.id} className="py-3 flex items-center gap-3" data-testid={`drawer-milestone-${m.id}`}>
                  <button onClick={() => toggleMilestone(m)} data-testid={`drawer-milestone-toggle-${m.id}`} className="shrink-0">
                    {m.completed ? <CheckCircle2 size={20} className="text-[#FF3355]" /> : <Circle size={20} className="text-[#C5C6C7]/40" />}
                  </button>
                  <div className="flex-1">
                    <div className={`font-mono text-sm ${m.completed ? "line-through text-[#C5C6C7]" : "text-[#F5F5F7]"}`}>{m.title}</div>
                    {m.due_date && <div className="font-mono text-[10px] text-[#C5C6C7]/60">due {new Date(m.due_date).toLocaleDateString()}</div>}
                  </div>
                  <button onClick={() => deleteMilestone(m)} data-testid={`drawer-milestone-delete-${m.id}`} className="text-[#C5C6C7]/40 hover:text-[#FF9D9D]"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
            <form onSubmit={addMilestone} className="mt-4 flex gap-2 flex-wrap" data-testid="drawer-add-milestone-form">
              <input value={milestoneTitle} onChange={(e) => setMilestoneTitle(e.target.value)} placeholder="new milestone title" data-testid="drawer-milestone-title" className="flex-1 min-w-[200px] bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm" />
              <input type="date" value={milestoneDue} onChange={(e) => setMilestoneDue(e.target.value)} data-testid="drawer-milestone-due" className="bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#C5C6C7]" />
              <button type="submit" data-testid="drawer-milestone-add" className="bg-[#B91C2C] text-[#0B0C10] px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">add</button>
            </form>
          </div>

          {/* Activity */}
          <div className="border border-[#1F2833] p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">// ACTIVITY · {activity.length}</div>
            <ol className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {activity.map((a) => (
                <li key={a.id} className="font-mono text-xs text-[#F5F5F7]" data-testid={`drawer-activity-${a.id}`}>
                  <span className="text-[#FF3355]">[{new Date(a.created_at).toLocaleString()}]</span> {a.message}
                </li>
              ))}
            </ol>
            <form onSubmit={addActivity} className="mt-4 flex gap-2" data-testid="drawer-add-activity-form">
              <input value={activityMsg} onChange={(e) => setActivityMsg(e.target.value)} placeholder="post an update..." data-testid="drawer-activity-msg" className="flex-1 bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm" />
              <button type="submit" data-testid="drawer-activity-post" className="bg-[#B91C2C] text-[#0B0C10] px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">post</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ small helpers ============ */
function Field({ label, value, onChange, type = "text", testid, required, textarea, min, max, minLength }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">&gt; {label}</label>
      {textarea ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#F5F5F7]"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          required={required}
          minLength={minLength}
          min={min}
          max={max}
          className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#F5F5F7]"
        />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">&gt; {label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#F5F5F7]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function EmptyBlock({ title, hint }) {
  return (
    <div className="border border-[#1F2833] p-10 text-center">
      <Folder size={28} className="text-[#B91C2C] mx-auto" />
      <h3 className="mt-4 font-display text-xl">{title}</h3>
      {hint && <p className="mt-2 text-sm text-[#C5C6C7]">{hint}</p>}
    </div>
  );
}


/* ============ INSIGHTS TAB ============ */
function InsightsTab({ insights, onCreate, onEdit, onDelete }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// INSIGHTS · {insights.length}</div>
        <button onClick={onCreate} data-testid="admin-new-insight" className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]">
          <Plus size={14} /> new insight
        </button>
      </div>
      {insights.length === 0 ? (
        <EmptyBlock title="No insights published yet." hint="Create your first post — it appears on /insights immediately." />
      ) : (
        <div className="border border-[#1F2833]">
          {insights.map((i) => (
            <div key={i.id} className="p-5 border-b border-[#1F2833] last:border-b-0 flex items-start gap-4 flex-wrap" data-testid={`admin-insight-${i.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] mb-1">
                  {INSIGHT_CATEGORIES.find((c) => c.slug === i.category)?.label || i.category}
                  {!i.published && <span className="ml-2 text-[#FFCB6B]">· DRAFT</span>}
                </div>
                <div className="font-display text-lg font-bold">{i.title}</div>
                <div className="font-mono text-xs text-[#C5C6C7] mt-1">{i.slug} · {i.author}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onEdit(i)} data-testid={`admin-insight-edit-${i.id}`} className="border border-[#1F2833] hover:border-[#B91C2C] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">edit</button>
                <button onClick={() => onDelete(i)} data-testid={`admin-insight-delete-${i.id}`} className="border border-[#1F2833] hover:border-[#FF9D9D] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] hover:text-[#FF9D9D]"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ AI PROGRESS UPDATE ============ */
function AiUpdateBlock({ projectId, onPosted }) {
  const [notes, setNotes] = useState("");
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const encoded = await Promise.all(files.map(readFile));
    setShots(encoded);
  };

  const generate = async (e) => {
    e.preventDefault();
    if (!notes.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/updates`, {
        project_id: projectId,
        notes: notes.trim(),
        screenshots: shots,
      });
      setPreview(data);
      setNotes("");
      setShots([]);
      onPosted?.();
      toast.success("AI update published — visible to client.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "AI update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#1F2833] p-5">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">// AI PROGRESS UPDATE</div>
        <div className="font-mono text-[10px] text-[#C5C6C7]">Claude — Emergent</div>
      </div>
      <form onSubmit={generate} className="mt-4 space-y-3">
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="// bullet notes for AI to expand into a client-ready update..."
          data-testid="ai-update-notes"
          className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-3 py-2 font-mono text-sm text-[#F5F5F7]"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept="image/*" multiple onChange={onFiles} data-testid="ai-update-files" className="font-mono text-xs text-[#C5C6C7]" />
          {shots.length > 0 && <span className="font-mono text-[10px] text-[#FF3355]">{shots.length} attached</span>}
          <button type="submit" disabled={busy || !notes.trim()} data-testid="ai-update-generate" className="ml-auto bg-[#B91C2C] hover:bg-[#FF3355] text-[#F5F5F7] px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] font-bold disabled:opacity-50">
            {busy ? "Generating..." : "[ Generate + Publish ]"}
          </button>
        </div>
      </form>
      {preview && (
        <div className="mt-4 border border-[#1F2833] p-4 text-sm text-[#C5C6C7]">
          <div className="font-display text-lg text-[#F5F5F7]">{preview.title}</div>
          <div className="mt-1 text-[#FF3355]">{preview.summary}</div>
        </div>
      )}
    </div>
  );
}

function ApplicationsTab({ applications }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// APPLICATIONS · {applications.length}</div>
      {applications.length === 0 ? (
        <EmptyBlock title="No applications yet." hint="Submissions from /careers/apply will appear here." />
      ) : (
        <div className="space-y-4">
          {applications.map((a) => (
            <div key={a.id} className="border border-[#1F2833] p-6" data-testid={`admin-application-${a.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-lg font-bold">{a.name}</div>
                  <div className="font-mono text-xs text-[#C5C6C7]">{a.email} {a.phone ? `· ${a.phone}` : ""} {a.location ? `· ${a.location}` : ""}</div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              <div className="mt-3 font-mono text-xs text-[#B91C2C]">
                {a.role} {a.experience_years != null ? `· ${a.experience_years}y exp` : ""}
              </div>
              {(a.portfolio_url || a.linkedin_url) && (
                <div className="mt-2 font-mono text-xs flex gap-3">
                  {a.portfolio_url && <a href={a.portfolio_url} target="_blank" rel="noreferrer" className="text-[#FF3355] hover:underline">portfolio ↗</a>}
                  {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="text-[#FF3355] hover:underline">linkedin ↗</a>}
                </div>
              )}
              <p className="mt-3 text-sm text-[#C5C6C7] whitespace-pre-wrap">{a.cover_letter}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ INSIGHT MODAL ============ */
function InsightModal({ insight, onClose, onSaved }) {
  const [form, setForm] = useState(
    insight || {
      title: "", slug: "", category: INSIGHT_CATEGORIES[0].slug,
      excerpt: "", body: "", author: "TraceElements",
      cover_url: "", published: true,
    }
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.cover_url) delete payload.cover_url;
      if (insight?.id) {
        await api.patch(`/admin/insights/${insight.id}`, payload);
      } else {
        await api.post("/admin/insights", payload);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0C10]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="te-terminal-window w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="insight-modal">
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355]">{insight ? "// EDIT.INSIGHT" : "// NEW.INSIGHT"}</div>
          <button onClick={onClose} className="text-[#C5C6C7] hover:text-[#F5F5F7]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 lg:p-8 space-y-5">
          <Field label="title" value={form.title} onChange={(v) => setForm({ ...form, title: v, slug: form.slug || slugify(v) })} testid="insight-title" required />
          <Field label="slug (url path)" value={form.slug} onChange={(v) => setForm({ ...form, slug: slugify(v) })} testid="insight-slug" required />
          <SelectField label="category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="insight-category" options={INSIGHT_CATEGORIES.map((c) => ({ value: c.slug, label: c.label }))} />
          <Field label="author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} testid="insight-author" />
          <Field label="excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="insight-excerpt" textarea />
          <Field label="body (markdown-lite: ## headings, **bold**, `code`)" value={form.body} onChange={(v) => setForm({ ...form, body: v })} testid="insight-body" textarea />
          <Field label="cover image url (optional)" value={form.cover_url || ""} onChange={(v) => setForm({ ...form, cover_url: v })} testid="insight-cover" />
          <label className="flex items-center gap-3 font-mono text-xs text-[#C5C6C7]">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="insight-published" className="accent-[#FF3355]" />
            published (visible on /insights)
          </label>
          <button type="submit" disabled={busy} data-testid="insight-submit" className="w-full bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50">
            {busy ? "SAVING..." : insight ? "[ SAVE CHANGES ]" : "[ PUBLISH INSIGHT ]"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ============ SITE TOGGLES TAB ============ */
const LANDING_TOGGLES = [
  {
    key: "impact_page_enabled",
    name: "Impact & Endowment Engine",
    path: "/impact",
    desc: "Non-profit cohort sales landing page. When switched off, visitors are silently redirected to the homepage.",
  },
  {
    key: "launch_page_enabled",
    name: "Commercial Revenue Engine",
    path: "/launch",
    desc: "Commercial sales-funnel landing page. When switched off, visitors are silently redirected to the homepage.",
  },
];

function SiteTogglesTab() {
  const [flags, setFlags] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/settings/feature-flags")
      .then((r) => setFlags(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
  }, []);

  const toggleFlag = async (key) => {
    if (!flags || busyKey) return;
    setBusyKey(key);
    try {
      const r = await api.patch("/settings/feature-flags", { [key]: !flags[key] });
      setFlags(r.data);
      toast.success(r.data[key] ? "Page is now LIVE." : "Page is now OFFLINE — visitors redirect to home.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-toggles-tab">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// SITE.FEATURES</div>
      {LANDING_TOGGLES.map((t) => {
        const enabled = flags ? flags[t.key] !== false : null;
        return (
          <div key={t.key} className="border border-[#1F2833] p-6 flex flex-wrap items-center justify-between gap-6" data-testid={`toggle-card-${t.key}`}>
            <div className="max-w-xl">
              <div className="flex items-center gap-3">
                <h3 className="font-display text-lg font-bold tracking-tight">{t.name}</h3>
                <span
                  data-testid={`toggle-status-${t.key}`}
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                    enabled
                      ? "border-[#34d399]/50 text-[#34d399]"
                      : "border-[#C5C6C7]/30 text-[#C5C6C7]"
                  }`}
                >
                  {enabled === null ? "…" : enabled ? "● live" : "○ offline"}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#C5C6C7] leading-relaxed">
                <a href={t.path} target="_blank" rel="noreferrer" className="text-[#FF3355] hover:underline" data-testid={`toggle-preview-${t.key}`}>{t.path}</a>
                {" "}· {t.desc}
              </p>
            </div>
            <button
              onClick={() => toggleFlag(t.key)}
              disabled={enabled === null || busyKey === t.key}
              data-testid={`toggle-switch-${t.key}`}
              role="switch"
              aria-checked={!!enabled}
              className={`relative h-8 w-16 border transition-colors duration-300 disabled:opacity-50 ${
                enabled ? "bg-[#B91C2C] border-[#FF3355]" : "bg-[#0B0C10] border-[#1F2833]"
              }`}
            >
              <span
                className={`absolute top-1 h-6 w-6 bg-[#F5F5F7] transition-all duration-300 ${
                  enabled ? "left-[calc(100%-1.75rem)]" : "left-1"
                }`}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}
